/**
 * Quotation process management
 *
 * @author Pablo <pablo@gestionaleauto.com>
 */

/**
 * Main
 */
import React, { useEffect } from 'react';

/**
 * Material UI
 */
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';

/**
 * Redux
 */
import { useDispatch, useSelector } from 'react-redux';
import {
  resetSuccess, setStep, setSubStep, wizardState, validateForm,
} from '../redux/wizardSlice';
import { leadState } from '../redux/leadSlice';
import { lookupsState } from '../redux/lookupsSlice';

/**
 * Components
 */
import Navigation from './wizard/Navigation';
import PersonalData from './wizard/PersonalData';
import TradeInWizard from './wizard/TradeInWizard';
import Resume from './wizard/tradeIn/Resume';
import { steps } from '../helpers/const';

const useStyles = makeStyles((theme) => ({
  panel: {
    margin: '35px 0',
    textAlign: 'center',
  },
  panelTitle: {
    fontSize: '1.5em',
    lineHeight: '1.2em',
    fontWeight: 'bold',
    marginBottom: 5,
    [theme.breakpoints.up('md')]: {
      fontSize: '1.8em',
    },
  },
}));

/**
 * @returns {JSX.Element}
 */
function QuotationWizard() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { personalData, quoteSteps: subStepsState } = useSelector(leadState);
  const { quote: quoteLookup } = useSelector(lookupsState);
  const { tradeIn: hasTradeIn } = personalData;
  const { validStep, step, subStep } = useSelector(wizardState);

  useEffect(() => {
    const element = document.getElementById('ga-quotation-data');
    element.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
  }, [step, subStep]);

  const getTitle = () => {
    if (step === steps.resume) {
      return 'Congratulations';
    }
    if (step === steps.personal_data) {
      return 'Send your request';
    }
    if (step === steps.resumeReadOnly) {
      return '';
    }

    switch (subStep) {
      case 0:
        return 'Which vehicle do you want to evaluate?';
      case 1:
        return 'Tell us something more';
      default:
        return 'We are almost done';
    }
  };

  const getSubTitle = () => {
    if (step === steps.resume) {
      return 'Here is the value of your vehicles';
    }
    if (step === steps.personal_data) {
      return 'Complete with your data';
    }
    if (step === steps.resumeReadOnly) {
      return '';
    }
    return 'Enter your vehicle data';
  };

  return (
    <>
      <div id="ga-quotation-data" className={classes.panel}>
        <p className={classes.panelTitle}>
          {t(getTitle())}
        </p>
        <p>
          {t(getSubTitle())}
        </p>
        {step === steps.personal_data && (
          <PersonalData
            showReceiveOffer
            showTradeFlag={false}
            showFinancialFlag={false}
          />
        )}
        {step === steps.trade && hasTradeIn && <TradeInWizard />}
        {step === steps.resume && <Resume updateOnStart />}
        {step === steps.resumeReadOnly && <Resume readOnly completeLead />}

      </div>
      {step === steps.trade
        && (
          <Navigation
            firstStep={step === steps.trade && subStep === 0 ? steps.trade : steps.personal_data}
            enablePrev={subStep !== 0}
            hidePrev={subStep === 0}
            enableNext={subStepsState[subStep].isValid}
            disabledNext={quoteLookup.versionsLoad}
            needSubmit={[2].includes(subStep)}
            callBackSuccess={() => {
              dispatch(validateForm(false));
              dispatch(setStep(0));
              dispatch(resetSuccess());
            }}
            callBackNext={() => {
              if (step === steps.trade
                && subStep < 2
                && subStepsState[subStep].isValid) {
                dispatch(validateForm(false));
                dispatch(setSubStep(subStep + 1));
                return;
              }
              if (step !== steps.trade
                || (step === steps.trade && subStep === 2)) {
                dispatch(setStep(step + 1));
              }
            }}
          />
        )}
      {step === steps.personal_data
        && (
          <Navigation
            firstStep={steps.trade}
            stepPrevSize={steps.trade}
            stepNextSize={steps.resume}
            enablePrev
            enableNext={validStep === true}
            needSubmit
          />
        )}
      {step === steps.resume
        && (
          <Navigation
            firstStep={steps.trade}
            stepPrevSize={steps.personal_data}
            stepNextSize={steps.resumeReadOnly}
            enablePrev
            enableNext={validStep === true}
            needSubmit
          />
        )}
    </>
  );
}
QuotationWizard.propTypes = {
};

QuotationWizard.defaultProps = {
};
export default QuotationWizard;
